import { inject } from '@angular/core';
import { CanActivateFn, Route, Router } from '@angular/router';
import { errorCodeResolver } from '@/pages/error/error-code.resolver';
import type { Role } from '@/models/role.model';
import { APP_ROUTES } from './routes.type';
import { ContainerComponent } from '@/components/container/container.component';
import { authGuard } from '@/auth.guard';
import { AuthStore } from '@/store/auth.store';
import { ensureUserLoadedGuard } from '@/ensure-user-loaded.guard';
import { profileCanDeactivateGuard } from '@/profile-can-deactivate.guard';
import { guestGuard } from '@/guest.guard';
import { roleGuard } from '@/role.guard';
import { userHistoryResolver } from '@/pages/history/user-history.resolver';
import { completionByIdResolver } from '@/pages/user-completion-view/my-completion.resolver';
import { publicDiagnosticEntryGuard } from '@/pages/public-diagnostic/public-diagnostic-entry.guard';

const redirectIfAuthenticatedGuard: CanActivateFn = () => {
  const authStore = inject(AuthStore);
  const router = inject(Router);
  if (authStore.isAuthenticated()) {
    return router.createUrlTree(['/', APP_ROUTES.DASHBOARD]);
  }
  return true;
};

const APP_MAIN_ROUTE: Route = {
  path: ``,
  canActivate: [redirectIfAuthenticatedGuard],
  loadComponent: () => import('../pages/main/main.component').then((c) => c.MainComponent),
  data: { scroll: 'top' },
};

const APP_PUBLIC_DIAGNOSTIC_ROUTE: Route = {
  path: APP_ROUTES.PUBLIC_DIAGNOSTIC,
  canActivate: [publicDiagnosticEntryGuard],
  loadComponent: () =>
    import('../pages/public-diagnostic/public-diagnostic.component').then(
      (c) => c.PublicDiagnosticComponent,
    ),
  data: { scroll: 'top' },
};

const APP_DASHBOARD_ROUTE: Route = {
  path: APP_ROUTES.DASHBOARD,
  canActivate: [authGuard],
  loadComponent: () =>
    import('../pages/dashboard/dashboard.component').then((c) => c.DashboardComponent),
  data: { scroll: 'top' },
};

const APP_PROFILE_ROUTE: Route = {
  path: APP_ROUTES.PROFILE,
  canActivate: [authGuard],
  loadComponent: () => import('../pages/profile/profile.component').then((c) => c.ProfileComponent),
  data: { scroll: 'top' },
  children: [
    { path: '', redirectTo: APP_ROUTES.PROFILE_TAB_SETTINGS, pathMatch: 'full' },
    {
      path: APP_ROUTES.PROFILE_TAB_SETTINGS,
      canDeactivate: [profileCanDeactivateGuard],
      loadComponent: () =>
        import('../pages/profile/settings/profile-settings.component').then(
          (c) => c.ProfileSettingsComponent,
        ),
    },
    {
      path: APP_ROUTES.PROFILE_TAB_SOCIAL,
      loadComponent: () =>
        import('../pages/profile/social/profile-social-tab.component').then(
          (c) => c.ProfileSocialTabComponent,
        ),
    },
    {
      path: APP_ROUTES.PROFILE_TAB_MODALITY,
      loadComponent: () =>
        import('../pages/profile/modality/profile-modality-tab.component').then(
          (c) => c.ProfileModalityTabComponent,
        ),
    },
  ],
};

const APP_SOCIAL_PROFILE_ROUTE: Route = {
  path: APP_ROUTES.SOCIAL_PROFILE,
  redirectTo: `${APP_ROUTES.PROFILE}/${APP_ROUTES.PROFILE_TAB_SOCIAL}`,
  pathMatch: 'full',
};

const APP_LOGIN_ROUTE: Route = {
  path: APP_ROUTES.LOGIN,
  canActivate: [guestGuard],
  loadComponent: () => import('../pages/login/login.component').then((c) => c.LoginPageComponent),
  data: { scroll: 'top' },
};

const APP_REGISTER_ROUTE: Route = {
  path: APP_ROUTES.REGISTER,
  canActivate: [guestGuard],
  loadComponent: () =>
    import('../pages/register/register.component').then((c) => c.RegisterComponent),
  data: { scroll: 'top' },
};

const APP_RESET_ROUTE: Route = {
  path: APP_ROUTES.RESET,
  canActivate: [guestGuard],
  loadComponent: () => import('../pages/reset/reset.component').then((c) => c.ResetComponent),
  data: { scroll: 'top' },
};

const APP_FORGOT_ROUTE: Route = {
  path: APP_ROUTES.FORGOT,
  canActivate: [guestGuard],
  loadComponent: () => import('../pages/forgot/forgot.component').then((c) => c.ForgotComponent),
  data: { scroll: 'top' },
};

const APP_VERIFY_EMAIL_ROUTE: Route = {
  path: APP_ROUTES.VERIFY_EMAIL,
  canActivate: [guestGuard],
  loadComponent: () =>
    import('../pages/verify-email/verify-email.component').then((c) => c.VerifyEmailComponent),
  data: { scroll: 'top' },
};

const APP_QUESTIONNAIRE_REDIRECT: Route = {
  path: APP_ROUTES.QUESTIONNAIRE,
  redirectTo: APP_ROUTES.QUESTIONNAIRE_MAIN,
  pathMatch: 'full',
};

const APP_QUESTIONNAIRE_PERSPECTIVE_REDIRECT: Route = {
  path: APP_ROUTES.QUESTIONNAIRE_PERSPECTIVE,
  redirectTo: APP_ROUTES.DAILY_REFLECTION,
  pathMatch: 'full',
};

const APP_QUESTIONNAIRE_RETROSPECTIVE_REDIRECT: Route = {
  path: APP_ROUTES.QUESTIONNAIRE_RETROSPECTIVE,
  redirectTo: APP_ROUTES.DAILY_REFLECTION,
  pathMatch: 'full',
};

const APP_QUESTIONNAIRE_TYPE_ROUTE: Route = {
  path: APP_ROUTES.QUESTIONNAIRE_TYPE,
  canActivate: [authGuard],
  loadComponent: () =>
    import('../pages/questionnaire/questionnaire.component').then((c) => c.QuestionnaireComponent),
  data: { scroll: 'top' },
};

const APP_MORNING_REFLECTION_REDIRECT: Route = {
  path: APP_ROUTES.MORNING_REFLECTION,
  redirectTo: APP_ROUTES.DAILY_REFLECTION,
  pathMatch: 'full',
};

const APP_DAILY_REFLECTION_ROUTE: Route = {
  path: APP_ROUTES.DAILY_REFLECTION,
  canActivate: [authGuard],
  loadComponent: () =>
    import('../pages/daily-reflection/daily-reflection-shell.component').then(
      (c) => c.DailyReflectionShellComponent,
    ),
  children: [
    {
      path: '',
      loadComponent: () =>
        import('../pages/daily-reflection/daily-reflection-hub.component').then(
          (c) => c.DailyReflectionHubComponent,
        ),
      data: { scroll: 'top' },
    },
    {
      path: APP_ROUTES.DAILY_REFLECTION_PERSPECTIVE_CHILD,
      loadComponent: () =>
        import('../pages/questionnaire/questionnaire.component').then(
          (c) => c.QuestionnaireComponent,
        ),
      data: {
        scroll: 'top',
        type: 'perspective',
        skipIntro: true,
      },
    },
    {
      path: APP_ROUTES.DAILY_REFLECTION_RETROSPECTIVE_CHILD,
      loadComponent: () =>
        import('../pages/questionnaire/questionnaire.component').then(
          (c) => c.QuestionnaireComponent,
        ),
      data: {
        scroll: 'top',
        type: 'retrospective',
        skipIntro: true,
      },
    },
  ],
};

const APP_MY_COMPLETION_VIEW_ROUTE: Route = {
  path: APP_ROUTES.MY_COMPLETION_VIEW,
  canActivate: [authGuard],
  resolve: { completion: completionByIdResolver },
  loadComponent: () =>
    import('../pages/user-completion-view/user-completion-view.component').then(
      (c) => c.UserCompletionViewComponent,
    ),
  data: { scroll: 'top' },
};

const APP_MY_COMPLETION_PRACTICES_ROUTE: Route = {
  path: APP_ROUTES.MY_COMPLETION_PRACTICES,
  redirectTo: ({ params }) => `${APP_ROUTES.HISTORY}/${params['completionId']}`,
};

const APP_HISTORY_ROUTE: Route = {
  path: APP_ROUTES.HISTORY,
  canActivate: [authGuard],
  loadComponent: () => import('../pages/history/history.component').then((c) => c.HistoryComponent),
  data: { scroll: 'top' },
};

const APP_SUPPORT_LIST_ROUTE: Route = {
  path: APP_ROUTES.SUPPORT,
  pathMatch: 'full',
  canActivate: [authGuard],
  loadComponent: () =>
    import('../pages/support-list/support-list.component').then((c) => c.SupportListComponent),
  data: { scroll: 'top' },
};

const APP_SUPPORT_CREATE_ROUTE: Route = {
  path: APP_ROUTES.SUPPORT_CREATE,
  canActivate: [authGuard],
  loadComponent: () =>
    import('../pages/support-create/support-create.component').then(
      (c) => c.SupportCreateComponent,
    ),
  data: { scroll: 'top' },
};

const APP_SUPPORT_TICKET_ROUTE: Route = {
  path: APP_ROUTES.SUPPORT_TICKET,
  canActivate: [authGuard],
  loadComponent: () =>
    import('../pages/support-ticket/support-ticket.component').then(
      (c) => c.SupportTicketComponent,
    ),
  data: { scroll: 'top' },
};

const APP_REFERENCE_ROUTE: Route = {
  path: APP_ROUTES.REFERENCE,
  canActivate: [authGuard],
  loadComponent: () =>
    import('../pages/reference/reference-shell.component').then((c) => c.ReferenceShellComponent),
  children: [
    {
      path: '',
      loadComponent: () =>
        import('../pages/reference/reference.component').then((c) => c.ReferenceComponent),
      data: { scroll: 'top' },
    },
    {
      path: APP_ROUTES.REFERENCE_POST_CHILD,
      loadComponent: () =>
        import('../pages/reference/reference-post.component').then((c) => c.ReferencePostComponent),
      data: { scroll: 'top' },
    },
  ],
};

const APP_BLOG_ROUTE: Route = {
  path: APP_ROUTES.BLOG,
  loadComponent: () =>
    import('../pages/blog/blog-shell.component').then((c) => c.BlogShellComponent),
  children: [
    {
      path: '',
      loadComponent: () =>
        import('../pages/blog/blog-index.component').then((c) => c.BlogIndexComponent),
      data: { scroll: 'top' },
    },
    {
      path: APP_ROUTES.BLOG_CATEGORY_CHILD,
      loadComponent: () =>
        import('../pages/blog/blog-category.component').then((c) => c.BlogCategoryComponent),
      data: { scroll: 'top' },
    },
    {
      path: APP_ROUTES.BLOG_POST_CHILD,
      loadComponent: () =>
        import('../pages/blog/blog-post.component').then((c) => c.BlogPostComponent),
      data: { scroll: 'top' },
    },
  ],
};

const APP_INFO_PAGE_ROUTE: Route = {
  path: APP_ROUTES.INFO_PAGE,
  loadComponent: () =>
    import('../pages/info-page/info-page.component').then((c) => c.InfoPageComponent),
  data: { scroll: 'top' },
};

const APP_ADMIN_INFO_PAGES_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_INFO_PAGES,
  pathMatch: 'full',
  canActivate: [authGuard, roleGuard(['admin'])],
  loadComponent: () =>
    import('../pages/admin-info-pages/admin-info-pages.component').then(
      (c) => c.AdminInfoPagesComponent,
    ),
  data: { scroll: 'top', title: 'Инфо-страницы' },
};

const APP_ADMIN_INFO_PAGE_CREATE_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_INFO_PAGE_CREATE,
  canActivate: [authGuard, roleGuard(['admin'])],
  loadComponent: () =>
    import('../pages/admin-info-page-form/admin-info-page-form.component').then(
      (c) => c.AdminInfoPageFormComponent,
    ),
  data: { scroll: 'top', title: 'Новая инфо-страница' },
};

const APP_ADMIN_INFO_PAGE_EDIT_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_INFO_PAGE_EDIT,
  canActivate: [authGuard, roleGuard(['admin'])],
  loadComponent: () =>
    import('../pages/admin-info-page-form/admin-info-page-form.component').then(
      (c) => c.AdminInfoPageFormComponent,
    ),
  data: { scroll: 'top', title: 'Редактирование инфо-страницы' },
};

const APP_ADMIN_REFERENCE_POST_CREATE_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_REFERENCE_POST_CREATE,
  canActivate: [authGuard, roleGuard(['admin'])],
  loadComponent: () =>
    import('../pages/admin-reference-post-form/admin-reference-post-form.component').then(
      (c) => c.AdminReferencePostFormComponent,
    ),
  data: { scroll: 'top', title: 'Новый пост справочника' },
};

const APP_ADMIN_REFERENCE_POST_EDIT_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_REFERENCE_POST_EDIT,
  canActivate: [authGuard, roleGuard(['admin'])],
  loadComponent: () =>
    import('../pages/admin-reference-post-form/admin-reference-post-form.component').then(
      (c) => c.AdminReferencePostFormComponent,
    ),
  data: { scroll: 'top', title: 'Редактирование поста справочника' },
};

const APP_ADMIN_REFERENCE_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_REFERENCE,
  pathMatch: 'full',
  canActivate: [authGuard, roleGuard(['admin'])],
  loadComponent: () =>
    import('../pages/admin-reference/admin-reference.component').then(
      (c) => c.AdminReferenceComponent,
    ),
  data: { scroll: 'top', title: 'Справочник' },
};

const APP_ADMIN_BLOG_POST_CREATE_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_BLOG_POST_CREATE,
  canActivate: [authGuard, roleGuard(['admin'])],
  loadComponent: () =>
    import('../pages/admin-blog-post-form/admin-blog-post-form.component').then(
      (c) => c.AdminBlogPostFormComponent,
    ),
  data: { scroll: 'top', title: 'Новый пост блога' },
};

const APP_ADMIN_BLOG_POST_EDIT_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_BLOG_POST_EDIT,
  canActivate: [authGuard, roleGuard(['admin'])],
  loadComponent: () =>
    import('../pages/admin-blog-post-form/admin-blog-post-form.component').then(
      (c) => c.AdminBlogPostFormComponent,
    ),
  data: { scroll: 'top', title: 'Редактирование поста блога' },
};

const APP_ADMIN_BLOG_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_BLOG,
  pathMatch: 'full',
  canActivate: [authGuard, roleGuard(['admin'])],
  loadComponent: () =>
    import('../pages/admin-blog/admin-blog.component').then((c) => c.AdminBlogComponent),
  data: { scroll: 'top', title: 'Блог' },
};

const APP_ERROR_ROUTE: Route = {
  path: `${APP_ROUTES.ERROR}/:code`,
  loadComponent: () => import('../pages/error/error.component').then((c) => c.ErrorComponent),
  resolve: { code: errorCodeResolver },
};

const APP_USER_HISTORY_COMPLETION_ROUTE: Route = {
  path: APP_ROUTES.USER_HISTORY_COMPLETION,
  canActivate: [authGuard, roleGuard(['moderator', 'admin'])],
  resolve: { user: userHistoryResolver, completion: completionByIdResolver },
  loadComponent: () =>
    import('../pages/user-completion-view/user-completion-view.component').then(
      (c) => c.UserCompletionViewComponent,
    ),
  data: { scroll: 'top' },
};

const APP_USER_HISTORY_COMPLETION_PRACTICES_ROUTE: Route = {
  path: APP_ROUTES.USER_HISTORY_COMPLETION_PRACTICES,
  redirectTo: ({ params }) =>
    `${APP_ROUTES.USERS}/${params['userId']}/${APP_ROUTES.HISTORY}/${params['completionId']}`,
};

const APP_USER_HISTORY_ROUTE: Route = {
  path: APP_ROUTES.USER_HISTORY,
  canActivate: [authGuard, roleGuard(['moderator', 'admin'])],
  resolve: { user: userHistoryResolver },
  loadComponent: () => import('../pages/history/history.component').then((c) => c.HistoryComponent),
  data: { scroll: 'top' },
};

const APP_USERS_ROUTE: Route = {
  path: APP_ROUTES.USERS,
  canActivate: [authGuard, roleGuard(['moderator', 'admin'])],
  loadComponent: () => import('../pages/users/users.component').then((c) => c.UsersComponent),
  data: { scroll: 'top' },
};

const APP_CONSULTANT_CLIENTS_ROUTE: Route = {
  path: APP_ROUTES.CONSULTANT_CLIENTS,
  canActivate: [authGuard, roleGuard(['consultant', 'moderator', 'admin'])],
  loadComponent: () =>
    import('../pages/consultant-clients/consultant-clients.component').then(
      (c) => c.ConsultantClientsComponent,
    ),
  data: { scroll: 'top' },
};

const APP_ADMIN_PROCESSES_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_PROCESSES,
  canActivate: [authGuard, roleGuard(['moderator', 'admin'])],
  loadComponent: () =>
    import('../pages/admin-processes/admin-processes.component').then(
      (c) => c.AdminProcessesComponent,
    ),
  data: { scroll: 'top' },
};

const APP_ADMIN_SCALES_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_SCALES,
  canActivate: [authGuard, roleGuard(['moderator', 'admin'])],
  loadComponent: () =>
    import('../pages/admin-domains/admin-domains.component').then((c) => c.AdminDomainsComponent),
  data: { scroll: 'top' },
};

const APP_ADMIN_MODEL_CREATE_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_MODEL_CREATE,
  canActivate: [authGuard, roleGuard(['admin'])],
  loadComponent: () =>
    import('../pages/admin-model-create/admin-model-create.component').then(
      (c) => c.AdminModelCreateComponent,
    ),
  data: { scroll: 'top' },
};

/** До `admin/models/:id/edit`, иначе `modality-interpretations` попадёт в `:id`. */
const APP_ADMIN_MODALITY_INTERPRETATION_CREATE_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_MODALITY_INTERPRETATION_CREATE,
  canActivate: [authGuard, roleGuard(['admin'])],
  loadComponent: () =>
    import('../pages/admin-modality-interpretation-create/admin-modality-interpretation-create.component').then(
      (c) => c.AdminModalityInterpretationCreateComponent,
    ),
  data: { scroll: 'top' },
};

const APP_ADMIN_MODALITY_INTERPRETATION_EDIT_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_MODALITY_INTERPRETATION_EDIT,
  canActivate: [authGuard, roleGuard(['admin'])],
  loadComponent: () =>
    import('../pages/admin-modality-interpretation-edit/admin-modality-interpretation-edit.component').then(
      (c) => c.AdminModalityInterpretationEditComponent,
    ),
  data: { scroll: 'top' },
};

/** До `admin/models/:id/edit`, иначе `pr-domain-texts` попадёт в `:id`. */
const APP_ADMIN_PR_DOMAIN_TEXT_EDIT_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_PR_DOMAIN_TEXT_EDIT,
  canActivate: [authGuard, roleGuard(['admin'])],
  loadComponent: () =>
    import('../pages/admin-pr-domain-text-edit/admin-pr-domain-text-edit.component').then(
      (c) => c.AdminPrDomainTextEditComponent,
    ),
  data: { scroll: 'top' },
};

const APP_ADMIN_MODEL_EDIT_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_MODEL_EDIT,
  canActivate: [authGuard, roleGuard(['admin'])],
  loadComponent: () =>
    import('../pages/admin-model-edit/admin-model-edit.component').then(
      (c) => c.AdminModelEditComponent,
    ),
  data: { scroll: 'top' },
};

const APP_ADMIN_MODELS_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_MODELS,
  pathMatch: 'full',
  canActivate: [authGuard, roleGuard(['moderator', 'admin'])],
  loadComponent: () =>
    import('../pages/admin-models/admin-models.component').then((c) => c.AdminModelsComponent),
  data: { scroll: 'top' },
};

const APP_ADMIN_INTERVENTION_CREATE_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_INTERVENTION_CREATE,
  canActivate: [authGuard, roleGuard(['admin'])],
  loadComponent: () =>
    import('../pages/admin-intervention-form/admin-intervention-form.component').then(
      (c) => c.AdminInterventionFormComponent,
    ),
  data: { scroll: 'top' },
};

const APP_ADMIN_INTERVENTION_EDIT_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_INTERVENTION_EDIT,
  canActivate: [authGuard, roleGuard(['admin'])],
  loadComponent: () =>
    import('../pages/admin-intervention-form/admin-intervention-form.component').then(
      (c) => c.AdminInterventionFormComponent,
    ),
  data: { scroll: 'top' },
};

const APP_ADMIN_INTERVENTION_VIEW_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_INTERVENTION_VIEW,
  canActivate: [authGuard, roleGuard(['moderator', 'admin'])],
  loadComponent: () =>
    import('../pages/admin-intervention-view/admin-intervention-view.component').then(
      (c) => c.AdminInterventionViewComponent,
    ),
  data: { scroll: 'top' },
};

const APP_ADMIN_INTERVENTIONS_LAB_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_INTERVENTIONS_LAB,
  canActivate: [authGuard, roleGuard(['moderator', 'admin'])],
  loadComponent: () =>
    import('../pages/admin-interventions-lab/admin-interventions-lab.component').then(
      (c) => c.AdminInterventionsLabComponent,
    ),
  data: { scroll: 'top' },
};

const APP_ADMIN_INTERVENTIONS_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_INTERVENTIONS,
  pathMatch: 'full',
  canActivate: [authGuard, roleGuard(['moderator', 'admin'])],
  loadComponent: () =>
    import('../pages/admin-interventions/admin-interventions.component').then(
      (c) => c.AdminInterventionsComponent,
    ),
  data: { scroll: 'top' },
};

const APP_ADMIN_QUESTIONNAIRES_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_QUESTIONNAIRES,
  pathMatch: 'full',
  canActivate: [authGuard, roleGuard(['moderator', 'admin'])],
  loadComponent: () =>
    import('../pages/admin-questionnaires/admin-questionnaires.component').then(
      (c) => c.AdminQuestionnairesComponent,
    ),
  data: { scroll: 'top' },
};

const APP_ADMIN_QUESTIONNAIRE_VIEW_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_QUESTIONNAIRE_VIEW,
  canActivate: [authGuard, roleGuard(['moderator', 'admin'])],
  loadComponent: () =>
    import('../pages/admin-questionnaire-version-view/admin-questionnaire-version-view.component').then(
      (c) => c.AdminQuestionnaireVersionViewComponent,
    ),
  data: { scroll: 'top' },
};

const APP_ADMIN_QUESTIONNAIRE_ANALYSIS_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_QUESTIONNAIRE_ANALYSIS,
  canActivate: [authGuard, roleGuard(['moderator', 'admin'])],
  loadComponent: () =>
    import('../pages/quality-analysis/quality-analysis.component').then(
      (c) => c.QualityAnalysisComponent,
    ),
  data: { scroll: 'top' },
};

const APP_ADMIN_QUESTIONNAIRE_EDIT_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_QUESTIONNAIRE_EDIT,
  canActivate: [authGuard, roleGuard(['moderator', 'admin'])],
  loadComponent: () =>
    import('../pages/admin-questionnaire-unpublished-edit/admin-questionnaire-unpublished-edit.component').then(
      (c) => c.AdminQuestionnaireUnpublishedEditComponent,
    ),
  data: { scroll: 'top' },
};

const APP_ADMIN_ANSWERS_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_ANSWERS,
  canActivate: [authGuard, roleGuard(['moderator', 'admin'])],
  loadComponent: () =>
    import('../pages/admin-answers/admin-answers.component').then((c) => c.AdminAnswersComponent),
  data: { scroll: 'top' },
};

const APP_ADMIN_SUPPORT_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_SUPPORT,
  pathMatch: 'full',
  canActivate: [authGuard, roleGuard(['moderator', 'admin'])],
  loadComponent: () =>
    import('../pages/admin-support/admin-support.component').then((c) => c.AdminSupportComponent),
  data: { scroll: 'top', title: 'Обращения в поддержку' },
};

const APP_ADMIN_SUPPORT_TICKET_ROUTE: Route = {
  path: APP_ROUTES.ADMIN_SUPPORT_TICKET,
  canActivate: [authGuard, roleGuard(['moderator', 'admin'])],
  loadComponent: () =>
    import('../pages/admin-support-ticket/admin-support-ticket.component').then(
      (c) => c.AdminSupportTicketComponent,
    ),
  data: { scroll: 'top' },
};

const PLACEHOLDER_ROUTES_CONFIG: { path: string; roles: Role[]; title: string }[] = [
  {
    path: APP_ROUTES.CONSULTANT_PROFILES,
    roles: ['consultant', 'moderator', 'admin'],
    title: 'Профили подопечных',
  },
  {
    path: APP_ROUTES.CONSULTANT_RESULTS,
    roles: ['consultant', 'moderator', 'admin'],
    title: 'Результаты опросов',
  },
  {
    path: APP_ROUTES.CONSULTANT_ANALYTICS,
    roles: ['consultant', 'moderator', 'admin'],
    title: 'Аналитика',
  },
  {
    path: APP_ROUTES.CONSULTANT_REFERENCE,
    roles: ['consultant', 'moderator', 'admin'],
    title: 'Справочник',
  },
  {
    path: APP_ROUTES.MODERATOR_CONTENT,
    roles: ['moderator', 'admin'],
    title: 'Модерация контента',
  },
  { path: APP_ROUTES.MODERATOR_REPORTS, roles: ['moderator', 'admin'], title: 'Жалобы' },
  { path: APP_ROUTES.MODERATOR_AUDIT, roles: ['moderator', 'admin'], title: 'Аудит действий' },
  { path: APP_ROUTES.MODERATOR_LOGS, roles: ['moderator', 'admin'], title: 'Логи' },
  { path: APP_ROUTES.ADMIN_ROLES, roles: ['admin'], title: 'Управление ролями' },
  { path: APP_ROUTES.ADMIN_SETTINGS, roles: ['admin'], title: 'Системные настройки' },
];

const PLACEHOLDER_ROUTES: Route[] = PLACEHOLDER_ROUTES_CONFIG.map(({ path, roles, title }) => ({
  path,
  canActivate: [authGuard, roleGuard(roles)],
  loadComponent: () =>
    import('../pages/placeholder/placeholder.component').then((c) => c.PlaceholderComponent),
  data: { scroll: 'top', title },
}));

export const CONTAINER_ROUTE: Route = {
  path: '',
  component: ContainerComponent,
  canActivate: [ensureUserLoadedGuard],
  children: [
    APP_MAIN_ROUTE,
    APP_PUBLIC_DIAGNOSTIC_ROUTE,
    APP_DASHBOARD_ROUTE,
    APP_PROFILE_ROUTE,
    APP_SOCIAL_PROFILE_ROUTE,
    APP_QUESTIONNAIRE_REDIRECT,
    APP_QUESTIONNAIRE_PERSPECTIVE_REDIRECT,
    APP_QUESTIONNAIRE_RETROSPECTIVE_REDIRECT,
    APP_QUESTIONNAIRE_TYPE_ROUTE,
    APP_MORNING_REFLECTION_REDIRECT,
    APP_DAILY_REFLECTION_ROUTE,
    APP_MY_COMPLETION_VIEW_ROUTE,
    APP_MY_COMPLETION_PRACTICES_ROUTE,
    APP_HISTORY_ROUTE,
    APP_SUPPORT_LIST_ROUTE,
    APP_SUPPORT_CREATE_ROUTE,
    APP_SUPPORT_TICKET_ROUTE,
    APP_REFERENCE_ROUTE,
    APP_USER_HISTORY_COMPLETION_ROUTE,
    APP_USER_HISTORY_COMPLETION_PRACTICES_ROUTE,
    APP_USER_HISTORY_ROUTE,
    APP_USERS_ROUTE,
    APP_CONSULTANT_CLIENTS_ROUTE,
    APP_BLOG_ROUTE,
    APP_INFO_PAGE_ROUTE,
    APP_ADMIN_INFO_PAGE_CREATE_ROUTE,
    APP_ADMIN_INFO_PAGE_EDIT_ROUTE,
    APP_ADMIN_INFO_PAGES_ROUTE,
    ...PLACEHOLDER_ROUTES,
    APP_ADMIN_PROCESSES_ROUTE,
    APP_ADMIN_SCALES_ROUTE,
    APP_ADMIN_REFERENCE_POST_CREATE_ROUTE,
    APP_ADMIN_REFERENCE_POST_EDIT_ROUTE,
    APP_ADMIN_REFERENCE_ROUTE,
    APP_ADMIN_BLOG_POST_CREATE_ROUTE,
    APP_ADMIN_BLOG_POST_EDIT_ROUTE,
    APP_ADMIN_BLOG_ROUTE,
    APP_ADMIN_QUESTIONNAIRE_VIEW_ROUTE,
    APP_ADMIN_QUESTIONNAIRE_ANALYSIS_ROUTE,
    APP_ADMIN_QUESTIONNAIRE_EDIT_ROUTE,
    APP_ADMIN_QUESTIONNAIRES_ROUTE,
    APP_ADMIN_MODEL_CREATE_ROUTE,
    APP_ADMIN_MODALITY_INTERPRETATION_CREATE_ROUTE,
    APP_ADMIN_MODALITY_INTERPRETATION_EDIT_ROUTE,
    APP_ADMIN_PR_DOMAIN_TEXT_EDIT_ROUTE,
    APP_ADMIN_MODEL_EDIT_ROUTE,
    APP_ADMIN_MODELS_ROUTE,
    APP_ADMIN_INTERVENTION_CREATE_ROUTE,
    APP_ADMIN_INTERVENTION_EDIT_ROUTE,
    APP_ADMIN_INTERVENTIONS_LAB_ROUTE,
    APP_ADMIN_INTERVENTION_VIEW_ROUTE,
    APP_ADMIN_INTERVENTIONS_ROUTE,
    APP_ADMIN_ANSWERS_ROUTE,
    APP_ADMIN_SUPPORT_ROUTE,
    APP_ADMIN_SUPPORT_TICKET_ROUTE,
    APP_LOGIN_ROUTE,
    APP_REGISTER_ROUTE,
    APP_RESET_ROUTE,
    APP_FORGOT_ROUTE,
    APP_VERIFY_EMAIL_ROUTE,
    APP_ERROR_ROUTE,
    {
      path: '**',
      redirectTo: `${APP_ROUTES.ERROR}/404`,
      pathMatch: 'full',
    },
  ],
};
